<script lang="ts">
import { defineComponent } from 'vue'
import { mapActions, mapState } from 'vuex'
import { isEmpty, throttle } from 'lodash'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import NrButton from '@nextrequestco/components/src/components/NrButton'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import NrLoadingAnimation from '@nextrequestco/components/src/components/NrLoadingAnimation'
import NrSentry from '../../../utils/nr_sentry'
import API from '../../../vue_shared/api'

export default defineComponent({
  name: 'requester-info',
  components: {
    NrTooltip,
    NrIconButton,
    NrIcon,
    NrButton,
    NrModal,
    NrLoadingAnimation,
    NrSelect
  },

  props: {
    prettyId: { type: String },
    canSeeRequesterPanel: { type: Boolean, default: false },
    canSeeRequesterDetails: { type: Boolean, default: false },
    canSeeEmailStatuses: { type: Boolean, default: false },
    name: { type: String },
    email: { type: String },
    address: { type: String },
    phone: { type: String },
    company: { type: String }
  },

  data() {
    return {
      selectedRequester: {},
      emailStatuses: [],
      isLoadingEmailStatuses: false,
      showEmailStatuses: false
    }
  },

  mounted() {
    this.selectedRequester = this.request.requester || {}
  },

  computed: {
    ...mapState('request_show', [
      'dataFetchState',
      'dataOptions',
      'request',
      'currentAction',
      'filteredRequestLoading'
    ]),
    emailStatusesText() {
      if (this.showEmailStatuses) {
        return 'Hide email status'
      } else {
        return 'View email status'
      }
    },
    url() {
      return `/admin/users/${this.request.requester.id}`
    },
    hasAnyEmailStatuses() {
      return !isEmpty(this.emailStatuses)
    },
    emailAvailable(): boolean {
      return this.email !== 'Email Not Available'
    }
  },

  methods: {
    ...mapActions('request_show', [
      'updateRequester',
      'fetchDataOptions',
      'setCurrentAction'
    ]),

    handleDataTyping: throttle(function (searchText = '', dataType) {
      if (!dataType) return
      this.fetchDataOptions({ searchText, dataType })
    }, 400),

    handleDataScroll: throttle(function (dataType) {
      if (!dataType) return
      this.handleDataTyping(this.dataFetchState[dataType].search_text, dataType)
    }, 400),

    loadEmailStatuses: throttle(
      function () {
        this.isLoadingEmailStatuses = true

        API.fetchRequestEmailStatuses(this.prettyId)
          .then((response) => {
            this.emailStatuses = response.data
            this.isLoadingEmailStatuses = false
          })
          .catch(() => {
            this.$toasted.error(
              'Failed to retrieve requester email status information.'
            )
          })
      },
      5000,
      { trailing: false }
    ),

    handleSave() {
      if (!this.selectedRequester?.id) {
        return
      }

      this.updateRequester({
        prettyId: this.prettyId,
        requesterId: this.selectedRequester.id
      })
        .then(() => {
          this.$toasted.success('Requester updated successfully')
        })
        .catch((e) => {
          this.$toasted.error('There was an error updating the request')
          NrSentry.captureError(e)
        })
        .finally(() => {
          this.setCurrentAction('')
        })
    },

    toggleEmailStatuses() {
      this.showEmailStatuses = !this.showEmailStatuses
      if (this.showEmailStatuses) {
        this.loadEmailStatuses()
      }
    }
  }
})
</script>

<template>
  <article
    class="panel panel-section requester-panel p-bottom-4"
    v-if="canSeeRequesterPanel"
  >
    <p
      class="panel-heading has-background-light has-text-weight-semibold is-size-4"
    >
      Requester
    </p>
    <div class="panel-content" v-if="!filteredRequestLoading">
      <div
        class="panel-item is-flex is-align-items-center is-justify-content-space-between"
        :class="{ 'panel-item--editable': $can('manage', 'Request') }"
        v-if="name"
      >
        <div class="is-flex is-align-items-flex-start">
          <div class="requester-info-icon">
            <NrIcon name="user" aria-hidden="true" />
          </div>
          <span v-if="$can('read', 'admin_only_items')">
            <a
              :href="url"
              class="has-text-weight-semibold underline m-left-8 pendo-ignore"
              >{{ name }}
            </a>
          </span>
          <span v-else class="m-left-8 pendo-ignore">
            {{ name }}
          </span>
        </div>
        <NrTooltip
          label="Change requester"
          class="panel-item-edit"
          v-if="$can('manage', 'Request')"
        >
          <NrIconButton
            aria-label="Change requester"
            variant="minimal-neutral"
            :icon="{ name: 'pencil-alt', type: 'regular' }"
            @click="setCurrentAction('change-requester')"
          />
        </NrTooltip>
      </div>

      <div v-if="canSeeRequesterDetails">
        <div class="panel-item is-flex is-align-items-flex-start" v-if="email">
          <div class="requester-info-icon">
            <NrIcon name="envelope" aria-hidden="true" />
          </div>
          <span class="pendo-ignore m-left-8">{{ email }}</span>
        </div>
        <div
          class="panel-item is-flex is-align-items-flex-start"
          v-if="address"
        >
          <div class="requester-info-icon">
            <NrIcon name="mapmarker-alt" aria-hidden="true" />
          </div>
          <span class="m-left-8 pendo-ignore">{{ address }}</span>
        </div>
        <div class="panel-item is-flex is-align-items-flex-start" v-if="phone">
          <div class="requester-info-icon">
            <NrIcon name="phone" aria-hidden="true" />
          </div>
          <span class="m-left-8 pendo-ignore">{{ phone }}</span>
        </div>
        <div
          class="panel-item is-flex is-align-items-flex-start"
          v-if="company"
        >
          <div class="requester-info-icon">
            <NrIcon name="university" aria-hidden="true" />
          </div>
          <span class="m-left-8">{{ company }}</span>
        </div>

        <NrTooltip v-if="canSeeEmailStatuses && emailAvailable">
          <NrButton
            variant="bordered-neutral"
            class="m-bottom-16 m-top-16"
            @click="toggleEmailStatuses"
          >
            {{ emailStatusesText }}
          </NrButton>

          <template v-slot:label>
            <div class="email-statuses-tip">
              <p class="m-bottom-8">
                Sent: Email was successfully sent from NextRequest.
              </p>
              <p class="m-bottom-8">
                Delivered: The destination email server accepted the message.
                (This does not guarantee successful delivery to the inbox.)
              </p>
              <p class="m-bottom-8">
                Opened: The email was opened by the requester.
              </p>
            </div>
          </template>
        </NrTooltip>

        <template v-if="showEmailStatuses">
          <NrLoadingAnimation v-if="isLoadingEmailStatuses" :count="3" />

          <template v-else>
            <div class="requester-email-statuses" v-if="!hasAnyEmailStatuses">
              No email status records.
            </div>
            <div
              class="requester-email-statuses m-bottom-24"
              v-for="status in emailStatuses"
              :key="status.id"
            >
              <p>
                <strong>{{ status.label }}</strong>
              </p>
              <ul class="requester-email-statuses-events m-left-8 m-top-8">
                <li
                  v-for="event in status.events"
                  :key="event.id"
                  class="m-bottom-8"
                >
                  {{ event.action }}: {{ event.time }}
                </li>
              </ul>
            </div>
          </template>
        </template>
      </div>
    </div>

    <NrLoadingAnimation v-else :count="3" />

    <NrModal
      class="requester-panel-modal"
      primaryButtonText="Change requester"
      title="Change requester"
      :hasSecondaryButton="true"
      :show="currentAction === 'change-requester'"
      @on-close-click="setCurrentAction('')"
      @on-secondary-click="setCurrentAction('')"
      @on-primary-click="handleSave"
    >
      <form @submit.prevent.stop>
        <fieldset>
          <NrSelect
            placeholder="Search requesters"
            :options="dataOptions.requesters"
            :filterable="false"
            :loading="dataFetchState.requesters.fetching"
            :hasMore="!dataFetchState.requesters.end"
            v-model="selectedRequester"
            label="name_with_email"
            @open="handleDataTyping($event, 'requesters')"
            @load-more="handleDataScroll('requesters')"
            @search="handleDataTyping($event, 'requesters')"
          >
            <template v-slot:label> Search requesters </template>
          </NrSelect>
          <p class="help">
            Can't find the requester?
            <a href="/admin/users/new">Create a new requester account</a>.
          </p>
        </fieldset>
      </form>
    </NrModal>
  </article>
</template>

<style lang="scss">
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';
.field {
  a {
    color: #007ac2;
  }
}
.underline:hover {
  color: #1b34ff;
  text-decoration: underline;
}
.email-statuses-tip {
  max-width: 22rem;
  text-align: left;
}
.panel-section.requester-panel {
  .panel-item {
    padding-left: 0;
    padding-right: 0;
    &.panel-item--editable {
      padding-left: 0.75em;
      padding-right: 0.75em;
    }
  }
  .requester-info-icon {
    font-size: $font-size-5;
  }
  .requester-panel-modal {
    .nr-modal__content {
      min-height: 200px;
    }
  }
}
.help {
  display: block;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  a {
    color: #007ac2;
  }
}
</style>
